import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AutomationService } from '../services/automation.service';
import type { AutomationFilters, AutomationRule } from '../types/automation.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useAutomation(companyId: string, initialFilters: AutomationFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<AutomationFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const rulesQuery = useQuery({
    queryKey: ['automation-rules', companyId, filters],
    queryFn: () => AutomationService.getRules(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['automation-stats', companyId],
    queryFn: () => AutomationService.getAutomationStats(companyId),
    enabled: !!companyId,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string, isActive: boolean }) => 
      AutomationService.toggleRule(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] });
      queryClient.invalidateQueries({ queryKey: ['automation-stats'] });
      toast.success('Đã cập nhật trạng thái quy tắc');
    },
  });

  const runNowMutation = useMutation({
    mutationFn: (id: string) => AutomationService.runRuleNow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] });
      toast.success('Đã khởi chạy quy tắc thủ công');
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<AutomationRule>) => AutomationService.createRule({ ...data, company_id: companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] });
      queryClient.invalidateQueries({ queryKey: ['automation-stats'] });
      toast.success('Đã tạo quy tắc tự động');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...updates }: Partial<AutomationRule> & { id: string }) =>
      AutomationService.updateRule(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] });
      toast.success('Đã cập nhật quy tắc tự động');
    },
  });

  return {
    rules: rulesQuery.data?.data || [],
    total: rulesQuery.data?.total || 0,
    stats: statsQuery.data,
    isLoading: rulesQuery.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    toggleRule: toggleMutation.mutateAsync,
    runRuleNow: runNowMutation.mutateAsync,
    createRule: createMutation.mutateAsync,
    updateRule: updateMutation.mutateAsync,
    isToggling: toggleMutation.isPending,
    isRunning: runNowMutation.isPending,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}

export function useAutomationLogs(companyId: string, ruleId?: string) {
  const query = useQuery({
    queryKey: ['automation-logs', companyId, ruleId],
    queryFn: () => AutomationService.getLogs(companyId, ruleId),
    enabled: !!companyId,
  });

  return {
    ...query,
    logs: query.data || [],
  };
}
